import api, { ApiResponse } from 'api';
import Button from 'components/button/Button';
import FormTextField from 'components/form/FormTextField';
import { Modal } from 'components/modal/Modal';
import { endpoints } from 'endpoints.config';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import {
    apiErrors,
    merchantErrorMessage,
    MerchantCreateDetails,
    MerchantCredentials,
    MerchantFormValues,
    normalizeDomain,
    PAYMENT_SETUP_PENDING,
} from './models';

type Props = {
    onClose: () => void;
    onCreated: (merchantName: string, credentials: MerchantCredentials) => void;
    onSaved: () => void;
};

const initialValues: MerchantFormValues = {
    merchantName: '',
    domain: '',
    statementDescriptor: '',
    contactEmail: '',
    contactFirstName: '',
    contactLastName: '',
    settlementWalletAddress: '',
    completeWebhookUrl: '',
    transferWebhookUrl: '',
};

const httpsUrl = Yup.string()
    .trim()
    .max(1000, 'Too long')
    .test('https', 'Enter a full https:// address', (value) => {
        if (!value) return true;
        try {
            return new URL(value).protocol === 'https:';
        } catch {
            return false;
        }
    });

const validationSchema = Yup.object({
    merchantName: Yup.string().trim().required('Required').max(100, 'Too long'),
    domain: Yup.string()
        .trim()
        .required('Required')
        .test('domain', 'Enter the website domain, for example shop.com', (value) =>
            /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(normalizeDomain(value ?? ''))
        ),
    statementDescriptor: Yup.string()
        .trim()
        .required('Required')
        .max(25, 'Up to 25 characters')
        .matches(/^[A-Za-z0-9 .,&*'#\-/]+$/, "Letters, digits, spaces and . , & * ' # - / only"),
    contactEmail: Yup.string().trim().required('Required').email('Enter a valid email').max(100, 'Too long'),
    contactFirstName: Yup.string().trim().max(50, 'Too long'),
    contactLastName: Yup.string().trim().max(50, 'Too long'),
    settlementWalletAddress: Yup.string()
        .trim()
        .required('Required')
        .matches(/^0x[0-9a-fA-F]{40}$/, 'Enter a valid wallet address'),
    completeWebhookUrl: httpsUrl.required('Required'),
    transferWebhookUrl: httpsUrl,
});

const lowerFirst = (value?: string) => (value ? value.charAt(0).toLowerCase() + value.slice(1) : '');

const FormDefaults = ({ details }: { details: MerchantCreateDetails | null }) => {
    const { values, setFieldValue } = useFormikContext<MerchantFormValues>();
    const lastApplied = useRef({ complete: '', transfer: '' });

    useEffect(() => {
        if (details?.defaultSettlementWalletAddress && !values.settlementWalletAddress) {
            setFieldValue('settlementWalletAddress', details.defaultSettlementWalletAddress, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details]);

    useEffect(() => {
        if (!details) return;
        const domain = normalizeDomain(values.domain);
        const apply = (
            template: string | null,
            field: 'completeWebhookUrl' | 'transferWebhookUrl',
            key: 'complete' | 'transfer'
        ) => {
            if (!template) return;
            const current = values[field];
            if (current && current !== lastApplied.current[key]) return;
            const next = domain ? template.split('{domain}').join(domain) : '';
            lastApplied.current[key] = next;
            if (next !== current) setFieldValue(field, next, false);
        };
        apply(details.completeWebhookUrlTemplate, 'completeWebhookUrl', 'complete');
        apply(details.transferWebhookUrlTemplate, 'transferWebhookUrl', 'transfer');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details, values.domain]);

    return null;
};

export const AddMerchantModal = ({ onClose, onCreated, onSaved }: Props) => {
    const [details, setDetails] = useState<MerchantCreateDetails | null>(null);

    useEffect(() => {
        api.get<ApiResponse<MerchantCreateDetails>>(endpoints.merchantManagement.createDetails)
            .then((res) => {
                if (res.data.status === '1' && res.data.details) setDetails(res.data.details);
            })
            .catch(() => undefined);
    }, []);

    const onSubmit = async (values: MerchantFormValues, helpers: FormikHelpers<MerchantFormValues>) => {
        const payload = { ...values, domain: normalizeDomain(values.domain) };
        try {
            const res = await api.post<ApiResponse<MerchantCredentials>>(
                endpoints.merchantManagement.create,
                payload
            );
            if (res.data.status === '1' && res.data.details) {
                onCreated(values.merchantName.trim(), res.data.details);
                return;
            }
            Toast.openToastMessage(merchantErrorMessage(res.data.errors?.[0]?.messageCode), ToastMessageReason.ERROR);
        } catch (ex: any) {
            const errors = apiErrors(ex);
            const code = errors[0]?.messageCode;
            if (code === PAYMENT_SETUP_PENDING) {
                Toast.openToastMessage(merchantErrorMessage(code), ToastMessageReason.PENDING);
                onSaved();
                return;
            }
            errors.forEach((e) => {
                const field = lowerFirst(e.fieldName);
                if (field in values) helpers.setFieldError(field, merchantErrorMessage(e.messageCode));
            });
            Toast.openToastMessage(merchantErrorMessage(code), ToastMessageReason.ERROR);
            if (code === 'Merchant_Setup_Failed') onSaved();
        }
    };

    return (
        <Modal
            className="AddMerchantModal"
            title="Add merchant"
            onCloseModal={onClose}
            closeOnClickOutside={false}
        >
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ isSubmitting }) => (
                    <Form className="AddMerchantForm">
                        <FormDefaults details={details} />
                        <h4>Merchant</h4>
                        <FormTextField
                            field="merchantName"
                            label="Merchant name"
                            disabled={isSubmitting}
                            maxLength={100}
                        />
                        <FormTextField
                            field="domain"
                            label="Website domain"
                            placeholder="shop.com"
                            disabled={isSubmitting}
                        />
                        <FormTextField
                            field="statementDescriptor"
                            label="Statement descriptor"
                            hint="Shown on the shopper's card statement"
                            disabled={isSubmitting}
                            maxLength={25}
                        />
                        <h4>Merchant login</h4>
                        <FormTextField
                            field="contactEmail"
                            label="Email"
                            type="email"
                            hint="The merchant portal login is sent to this email"
                            disabled={isSubmitting}
                            maxLength={100}
                        />
                        <div className="AddMerchantRow">
                            <FormTextField
                                field="contactFirstName"
                                label="First name"
                                optionalTag="Optional"
                                disabled={isSubmitting}
                                maxLength={50}
                            />
                            <FormTextField
                                field="contactLastName"
                                label="Last name"
                                optionalTag="Optional"
                                disabled={isSubmitting}
                                maxLength={50}
                            />
                        </div>
                        <h4>Settlement and notifications</h4>
                        <FormTextField
                            field="settlementWalletAddress"
                            label={`Settlement wallet${details?.settlementAssetCode ? ` (${details.settlementAssetCode})` : ''}`}
                            hint="Where this merchant's sales are paid out"
                            disabled={isSubmitting}
                        />
                        <FormTextField
                            field="completeWebhookUrl"
                            label="Order completed webhook URL"
                            disabled={isSubmitting}
                        />
                        <FormTextField
                            field="transferWebhookUrl"
                            label="Transfer webhook URL"
                            optionalTag="Optional"
                            disabled={isSubmitting}
                        />
                        <div className="ModalNavigation">
                            <Button type="button" priority="secondary" onClick={onClose} disabled={isSubmitting}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSubmitting} showSubmittingSpinner>
                                {isSubmitting ? 'Setting up merchant...' : 'Create merchant'}
                            </Button>
                        </div>
                        {isSubmitting && (
                            <p className="AddMerchantProgress">
                                Creating the merchant. This can take up to a minute.
                            </p>
                        )}
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};
